// Theme Context for dark mode with manual selection
// Supports: 'light', 'dark', or 'system' (follows OS preference)

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { lightTheme, darkTheme } from './theme';

const ThemeContext = createContext({
  mode: 'light',
  themePreference: 'system',
  setThemePreference: () => {},
});

export function useThemeMode() {
  return useContext(ThemeContext);
}

const THEME_STORAGE_KEY = 'sleeptronic-theme-preference';

export function ThemeProvider({ children }) {
  // Theme preference: 'light', 'dark', or 'system'
  const [themePreference, setThemePreferenceState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      if (saved && ['light', 'dark', 'system'].includes(saved)) {
        return saved;
      }
    }
    return 'system';
  });

  // Get system preference
  const getSystemMode = useCallback(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }, []);

  // Actual mode based on preference
  const [systemMode, setSystemMode] = useState(getSystemMode);

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      setSystemMode(e.matches ? 'dark' : 'light');
    };

    // Add listener for real-time updates
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // Set theme preference and save to localStorage
  const setThemePreference = useCallback((preference) => {
    setThemePreferenceState(preference);
    if (typeof window !== 'undefined') {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  }, []);

  // Calculate actual mode
  const mode = useMemo(() => {
    if (themePreference === 'system') {
      return systemMode;
    }
    return themePreference;
  }, [themePreference, systemMode]);

  // Select the appropriate theme based on mode
  const theme = useMemo(() => {
    return mode === 'dark' ? darkTheme : lightTheme;
  }, [mode]);

  const contextValue = useMemo(() => ({
    mode,
    themePreference,
    setThemePreference,
  }), [mode, themePreference, setThemePreference]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
